import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import { Button } from '@/components/Button/Button';

import styles from './InstructorStatsToolbar.module.css';

interface InstructorStatsToolbarProps {
  // Who is viewing. A student gets only the switch, once solutions show, and
  // it changes only their own page: it is their review control, not an
  // instructor's.
  audience?: 'staff' | 'student';
  errorMessage?: string;
  hideStats: boolean;
  isDownloading?: boolean;
  isUpdating: boolean;
  onDownloadDetailedScores?(): void;
  onRegrade?(): void;
  onToggleHideStats(): void;
  onUpdateStats?(): void;
  showDownloadButton?: boolean;
  showRegradeButton?: boolean;
  showUpdateButton?: boolean;
  updateSucceeded?: boolean;
}

export function InstructorStatsToolbar({
  audience = 'staff',
  errorMessage,
  hideStats,
  isDownloading = false,
  isUpdating,
  onDownloadDetailedScores,
  onRegrade,
  onToggleHideStats,
  onUpdateStats,
  showDownloadButton = false,
  showRegradeButton = false,
  showUpdateButton = true,
  updateSucceeded = false,
}: InstructorStatsToolbarProps) {
  const prefersReducedMotion = useReducedMotion();
  const previousHideStats = useRef(hideStats);
  const previousUpdateSucceeded = useRef(updateSucceeded);
  // Says nothing until something changes. A student's page has their scores,
  // not the class's stats, to hide (issue #174).
  const [announcement, setAnnouncement] = useState('');
  const hiddenWhat = audience === 'student' ? 'Scores and solutions' : 'Stats and solutions';

  useEffect(() => {
    if (isUpdating) {
      setAnnouncement('Updating instructor stats.');
      return;
    }

    if (errorMessage) {
      setAnnouncement(errorMessage);
      return;
    }

    if (updateSucceeded && !previousUpdateSucceeded.current) {
      setAnnouncement('Instructor stats updated.');
    } else if (hideStats !== previousHideStats.current) {
      setAnnouncement(`${hiddenWhat} ${hideStats ? 'hidden' : 'shown'}.`);
    }
    previousHideStats.current = hideStats;
    previousUpdateSucceeded.current = updateSucceeded;
  }, [errorMessage, hiddenWhat, hideStats, isUpdating, updateSucceeded]);

  return (
    <section
      aria-label={audience === 'student' ? 'Review controls' : 'Instructor stats controls'}
      className={styles.toolbar}
    >
      <div className={styles.left}>
        <button
          aria-pressed={hideStats}
          className={styles.toggle}
          data-hidden={hideStats ? 'true' : 'false'}
          onClick={onToggleHideStats}
          type="button"
        >
          <span aria-hidden="true" className={styles.toggleTrack}>
            <motion.span
              animate={{ x: hideStats ? 14 : 0 }}
              className={styles.toggleThumb}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      damping: 28,
                      mass: 0.7,
                      stiffness: 420,
                      type: 'spring',
                    }
              }
            />
          </span>
          {/* One name in both states: aria-pressed says whether it is on. A
              label that flipped read "Show Stats and Solutions, pressed"
              while they were hidden (issue #112). */}
          <span className={styles.toggleLabel}>Hide Stats and Solutions</span>
        </button>
      </div>

      {(showUpdateButton && onUpdateStats) ||
      (showRegradeButton && onRegrade) ||
      (showDownloadButton && onDownloadDetailedScores) ? (
        <div className={styles.right}>
          {showDownloadButton && onDownloadDetailedScores ? (
            <Button
              disabled={isDownloading || isUpdating}
              onClick={onDownloadDetailedScores}
              variant="secondary"
            >
              {isDownloading ? 'Preparing…' : 'Download Detailed Scores'}
            </Button>
          ) : null}
          {showRegradeButton && onRegrade ? (
            <Button disabled={isUpdating} onClick={onRegrade} variant="secondary">
              Regrade
            </Button>
          ) : null}
          {showUpdateButton && onUpdateStats ? (
            <Button
              className={styles.updateButton}
              disabled={isUpdating}
              onClick={onUpdateStats}
              variant="primary"
            >
              {isUpdating ? 'Updating…' : 'Update Stats'}
            </Button>
          ) : null}
        </div>
      ) : null}

      <p aria-atomic="true" aria-live="polite" className={styles.liveRegion}>
        {announcement}
      </p>

      {errorMessage ? (
        <p className={styles.error} role="alert">
          {errorMessage}
        </p>
      ) : null}
    </section>
  );
}
